import { getContrast, readableColor } from 'polished';
import { useMemo } from 'react';
import { ThemeColors, useTheme } from 'styled-components';

type Color = keyof ThemeColors | string;
type PreferenceOverride = {
	/**
	 * The preferred foreground color.
	 * @see {@link Options.darkTextColor}
	 * @see {@link Options.lightTextColor}
	 */
	mode: 'light' | 'dark';
	/**
	 * The minimal contrast ratio for the foreground color to meet. Contrast ratios range from 1 to 21.
	 * If this contrast ratio is not met, it will fallback to the color with the highest contrast ratio.
	 * @see {@link https://www.w3.org/TR/WCAG20/#contrast-ratiodef}
	 */
	contrastRatio: number;
};

type Options = {
	/**
	 * The dark foreground color. Defaults to 'antracite'.
	 */
	darkTextColor?: Color;
	/**
	 * The light foreground color. Defaults to 'white'.
	 */
	lightTextColor?: Color;
	/**
	 * Optionally, one can specify a preference for the foreground color, given a minimal contrast ratio.
	 */
	preference?: PreferenceOverride;
};

/**
 * This hook is used to compute a foreground color that is readable on a given background color.
 * @param color The background color
 * @param options The options for contrast computation ({@link Options}).
 * @returns The foreground color that is readable on the background color
 */
export function useReadableColor(color: Color, options: Options = {}) {
	const inputColor = useThemeColor(color);
	const darkTextColor = useThemeColor(options.darkTextColor ?? 'antracite');
	const lightTextColor = useThemeColor(options.lightTextColor ?? 'white');

	return useMemo(() => {
		if (options.preference) {
			const preferredColor = options.preference.mode === 'light' ? lightTextColor : darkTextColor;
			const contrastRatio = getContrast(preferredColor, inputColor);

			if (contrastRatio >= options.preference.contrastRatio) {
				return preferredColor;
			}
		}

		return readableColor(inputColor, darkTextColor, lightTextColor, false);
	}, [inputColor, darkTextColor, lightTextColor, options.preference]);
}

function useThemeColor(color: Color) {
	const theme = useTheme();

	return useMemo(() => {
		return color in theme.colors ? theme.colors[color as keyof ThemeColors] : color;
	}, [color, theme]);
}
